import axios from 'axios';
import { API_URL, handleApiError } from '../utils/config';
// Helper function to get auth headers - matching the pattern used in orderService.js
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };
};
// Get all products (admin) — pass params for server pagination/filters (AdminCoupons pattern)
export const getAllProducts = async (paramsOrTimestamp) => {
  try {
    const params =
      typeof paramsOrTimestamp === 'object' && paramsOrTimestamp !== null
        ? { ...paramsOrTimestamp }
        : {};

    const response = await axios.get(`${API_URL}/products/admin/products`, {
      ...getAuthHeaders(),
      params,
    });
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

/**
 * Admin product counts for stats cards.
 * Pass the same filters as the product list (search, categoryFilter, stockFilter,
 * activeFilter, attributeFilter). Pagination is ignored server-side.
 */
export const getProductStats = async (params = {}) => {
  try {
    const response = await axios.get(`${API_URL}/products/admin/products/stats`, {
      ...getAuthHeaders(),
      params: params || {},
    });
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// ===== PUBLIC FUNCTIONS =====

// Search products (public)
export const searchProducts = async (query, page = 1, limit = 24, sort = 'default') => {
  try {
    const response = await axios.get(`${API_URL}/products/search`, {
      params: {
        q: query,
        page,
        limit,
        sort
      }
      // No auth headers needed for public search
    });
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Get products by category slug (public)
export const getProductsByCategory = async (slug, page = 1, limit = 24, sort = 'default', filters = {}) => {
  try {
    const params = { page, limit, sort, ...filters };
    const response = await axios.get(`${API_URL}/products/category/${slug}`, { params });
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Get homepage products (public)
export const getHomepageProducts = async (page = 1, limit = 24) => {
  try {
    const response = await axios.get(`${API_URL}/products/homepage`, { params: { page, limit } });
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Get product by slug (public)
export const getProductBySlug = async (slug) => {
  try {
    const response = await axios.get(`${API_URL}/products/${slug}`);
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    // Check if the error indicates the product is inactive
    if (error.response && error.response.status === 404 && error.response.data.isInactive) {
      throw new Error('Product is inactive'); // Throw specific error for inactive product
    }
    throw new Error(errorMessage);
  }
};

// Get similar products (public)
export const getSimilarProducts = async (productId) => {
  try {
    const response = await axios.get(`${API_URL}/products/similar/${productId}`);
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Get product color attributes (public)
export const getProductColorAttributes = async (productId) => {
  try {
    const response = await axios.get(`${API_URL}/products/colors/${productId}`);
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Get featured products (public)
export const fetchFeaturedProducts = async (page = 1, limit = 20) => {
  try {
    const response = await axios.get(`${API_URL}/products/featured`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// ===== BULK ACTIONS =====

// Bulk apply discount to products
export const bulkDiscountAll = async ({ percentage, target = 'all', productIds, filters }) => {
  try {
    // Build request data
    const data = { percentage };

    // Add target-specific data
    if (target === 'selected' && Array.isArray(productIds)) {
      data.target = 'selected';
      data.productIds = productIds;
    } else if (target === 'filtered' && filters) {
      data.target = 'filtered';
      data.filters = filters;
    } else if (target === 'instock') {
      data.target = 'instock';
    } else {
      data.target = 'all';
    }

    // Use the correct endpoint based on target for backwards compatibility
    let endpoint = `${API_URL}/products/admin/products/bulk/discount`;

    // For legacy endpoints
    if (target === 'all') {
      endpoint = `${API_URL}/products/admin/products/bulk/discount/all`;
    } else if (target === 'instock') {
      endpoint = `${API_URL}/products/admin/products/bulk/discount/instock`;
    } else if (target === 'selected' || target === 'filtered') {
      endpoint = `${API_URL}/products/admin/products/bulk/discount/targeted`;
    }

    const response = await axios.post(
      endpoint,
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// For backward compatibility - apply to in-stock products
export const bulkDiscountInStock = async ({ percentage }) => {
  return bulkDiscountAll({ percentage, target: 'instock' });
};

// Bulk remove discount from products
export const bulkRemoveDiscount = async ({ target = 'all', productIds, filters } = {}) => {
  try {
    // Build request data
    const data = {};

    // Add target-specific data
    if (target === 'selected' && Array.isArray(productIds)) {
      data.target = 'selected';
      data.productIds = productIds;
    } else if (target === 'filtered' && filters) {
      data.target = 'filtered';
      data.filters = filters;
    } else if (target === 'instock') {
      data.target = 'instock';
    } else {
      data.target = 'all';
    }

    // Use the correct endpoint based on target for backwards compatibility
    let endpoint = `${API_URL}/products/admin/products/bulk/discount/remove`;

    // For targeted operations (selected or filtered)
    if (target === 'selected' || target === 'filtered') {
      endpoint = `${API_URL}/products/admin/products/bulk/discount/remove/targeted`;
    }

    const response = await axios.post(
      endpoint,
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Bulk increase base price for products
export const bulkIncreasePrice = async ({ percentage, rounding, target = 'all', productIds, filters }) => {
  try {
    // Build request data
    const data = { percentage, rounding };

    // Add target-specific data
    if (target === 'selected' && Array.isArray(productIds)) {
      data.target = 'selected';
      data.productIds = productIds;
    } else if (target === 'filtered' && filters) {
      data.target = 'filtered';
      data.filters = filters;
    } else if (target === 'instock') {
      data.target = 'instock';
    } else {
      data.target = 'all';
    }

    // Use the correct endpoint based on target for backwards compatibility
    let endpoint = `${API_URL}/products/admin/products/bulk/price/increase`;

    // For targeted operations (selected or filtered)
    if (target === 'selected' || target === 'filtered') {
      endpoint = `${API_URL}/products/admin/products/bulk/price/increase/targeted`;
    }

    const response = await axios.post(
      endpoint,
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Bulk decrease base price for products
export const bulkDecreasePrice = async ({ percentage, rounding, target = 'all', productIds, filters }) => {
  try {
    // Build request data
    const data = { percentage, rounding };

    // Add target-specific data
    if (target === 'selected' && Array.isArray(productIds)) {
      data.target = 'selected';
      data.productIds = productIds;
    } else if (target === 'filtered' && filters) {
      data.target = 'filtered';
      data.filters = filters;
    } else if (target === 'instock') {
      data.target = 'instock';
    } else {
      data.target = 'all';
    }

    // Use the correct endpoint based on target for backwards compatibility
    let endpoint = `${API_URL}/products/admin/products/bulk/price/decrease`;

    // For targeted operations (selected or filtered)
    if (target === 'selected' || target === 'filtered') {
      endpoint = `${API_URL}/products/admin/products/bulk/price/decrease/targeted`;
    }

    const response = await axios.post(
      endpoint,
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Bulk assign attribute
export const bulkAssignAttribute = async (data) => {
  // data should contain { attributeId, target, productIds?, filters? }
  try {
    const response = await axios.post(
      `${API_URL}/products/admin/products/bulk/assign-attribute`, 
      data, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Bulk remove attribute
export const bulkRemoveAttribute = async (data) => {
  // data should contain { attributeId, target, productIds?, filters? }
  try {
    const response = await axios.post(
      `${API_URL}/products/admin/products/bulk/remove-attribute`, 
      data, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Bulk set total quantity for products
export const bulkSetTotalQuantity = async (data) => {
  // data should contain { quantity, target, productIds?, filters? }
  try {
    const response = await axios.post(
      `${API_URL}/products/admin/products/bulk/total-quantity`, 
      data, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Bulk set stock quantity for products
export const bulkSetStockQuantity = async (data) => {
  // data should contain { quantity, target, productIds?, filters? }
  try {
    const response = await axios.post(
      `${API_URL}/products/admin/products/bulk/stock-quantity`, 
      data, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Bulk set active status for products
export const bulkSetActiveStatus = async (data) => {
  // data should contain { active, target, productIds?, filters? }
  try {
    const response = await axios.post(
      `${API_URL}/products/admin/products/bulk/active-status`, 
      data, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Get product by ID (admin)
export const getProductById = async (productId) => {
  try {
    const response = await axios.get(`${API_URL}/products/admin/products/${productId}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Create new product (admin)
export const createProduct = async (productData) => {
  try {
    const response = await axios.post(`${API_URL}/products/admin/products`, productData, getAuthHeaders());
    return response.data;
  } catch (error) {
    // Check if it's a duplicate slug error
    if (error.response?.data?.error === 'DUPLICATE_SLUG') {
      throw new Error('Product slug already exists. Please use a unique slug.');
    }
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Update product (admin)
export const updateProduct = async (productId, productData) => {
  try {
    const response = await axios.put(`${API_URL}/products/admin/products/${productId}`, productData, getAuthHeaders());
    
    // Check if there's a warning about duplicate slug
    if (response.data.warning) {
      // Return the response with the warning so the UI can display it
      return {
        ...response.data,
        hasWarning: true
      };
    }
    
    return response.data;
  } catch (error) {
    // Check if it's a duplicate slug error during creation
    if (error.response?.data?.error === 'DUPLICATE_SLUG') {
      throw new Error('Product slug already exists. Please use a unique slug.');
    }
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Delete product (admin)
export const deleteProduct = async (productId) => {
  try {
    const response = await axios.delete(
      `${API_URL}/products/admin/products/${productId}`, 
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting product:', error);
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Duplicate product without images (admin)
export const duplicateProduct = async (productId) => {
  try {
    // First get the product data
    const productData = await getProductById(productId);
    
    if (!productData || !productData.product) {
      throw new Error('Product data not found');
    }
    
    const product = productData.product;
    
    
    // Get the product attributes
    let productAttributes = [];
    try {
      const attributesData = await getProductAttributes(productId);
      productAttributes = attributesData.attributes || [];
    } catch (error) {
      console.error('Error fetching product attributes:', error);
      // Continue with duplication even if attributes fetch fails
    }
    
    // Get the original product title
    const originalTitle = product.product_title || '';
    
    // Create the updated title based on whether it's JSON or not
    let updatedTitle;
    const titleIsJson = typeof originalTitle === 'string' && originalTitle.trim().startsWith('{');
    
    if (titleIsJson) {
      try {
        // ROBUST JSON HANDLING:
        // 1. Parse the JSON properly
        // 2. Create a new object with clear structure
        // 3. Preserve the original order of keys (important for backend)
        
        // Parse the original JSON title
        const titleObj = JSON.parse(originalTitle);
        
        // Create a totally new object to avoid reference issues
        const newTitleObj = {};
        
        // CRITICAL: Ensure EN is the first property (some backends require this)
        newTitleObj.EN = `${titleObj.EN || ''} (Copy)`;
        
        // Add all other language keys, preserving their order
        Object.keys(titleObj).forEach(key => {
          if (key !== 'EN') {
            newTitleObj[key] = `${titleObj[key] || ''} (Copy)`;
          }
        });
        
        // Convert to exact JSON format with proper escaping
        updatedTitle = JSON.stringify(newTitleObj);
        
        // Double-validate - ensure the EN field is properly included
        const validationCheck = JSON.parse(updatedTitle);
        if (!validationCheck.EN) {
          // Force EN field if somehow missing after our operations
          validationCheck.EN = `${titleObj.EN || ''} (Copy)`;
          updatedTitle = JSON.stringify(validationCheck);
        }
        
      } catch (e) {
        console.error('Error manipulating JSON title:', e);
        // Fallback to simple string append
        updatedTitle = `${originalTitle} (Copy)`;
      }
    } else {
      // For non-JSON titles, just append (Copy)
      updatedTitle = `${originalTitle} (Copy)`;
      
    }
    
    // Create a direct, hardcoded title object as a last resort if needed
    const directEN = product.product_title && typeof product.product_title === 'string' && 
                    product.product_title.includes("PADDED COTTON HEADBAND");
    
    if (directEN) {
      // This is a known product format, use direct string replacement to force it
      const titleParts = originalTitle.split('"EN":"');
      if (titleParts.length > 1) {
        const afterEN = titleParts[1];
        const endOfENValue = afterEN.indexOf('"');
        if (endOfENValue > 0) {
          const ENValue = afterEN.substring(0, endOfENValue);
          const newENValue = `${ENValue} (Copy)`;
          updatedTitle = originalTitle.replace(`"EN":"${ENValue}"`, `"EN":"${newENValue}"`);
          
        }
      }
    }
    
    // Create a completely fresh product object and only copy essential fields
    // This avoids any issues with extra fields or nested objects
    const newProductData = {
      // Required fields
      product_title: updatedTitle,
      product_slug: `${product.product_slug}-copy-${Date.now().toString().slice(-6)}`,
      product_price: parseFloat(product.product_price || 0),
      product_price_discounted: parseFloat(product.product_price_discounted || product.product_price || 0),
      
      // Clear image-related fields
      product_image: null,
      
      // Important fields to copy
      category_id: parseInt(product.category_id || 0),
      product_description: product.product_description || '',
      meta_desc: product.meta_desc || '',
      meta_keywords: product.meta_keywords || '',
      quantity_stock: 0,
      product_quantity: parseInt(product.product_quantity || 0),
      weight: product.weight || 0,
      
      // Important flags
      active: true,
      homepage: product.homepage || false,
      is_giftcard: product.is_giftcard || false,
      enable_comment: product.enable_comment || false,
      enable_comment_custom: product.enable_comment_custom || false,
      
      // Make sure product_id is not included
      product_id: null,
      id: null
    };
    
    
    // Validate product data
    if (!newProductData.product_title) {
      console.error('Product title is missing before API call');
      throw new Error('Product title is required');
    }
    
    // Validate JSON structure one more time
    if (typeof newProductData.product_title === 'string' && 
        newProductData.product_title.startsWith('{')) {
      try {
        const parsedTitle = JSON.parse(newProductData.product_title);
        
        // Verify EN field exists
        if (!parsedTitle.EN) {
          console.error('EN field is missing in JSON title!', parsedTitle);
        } else {
        }
      } catch (e) {
        console.error('Invalid JSON in product_title before API call:', e);
      }
    }
    
    // Save the new product
    const response = await axios.post(
      `${API_URL}/products/admin/products`, 
      newProductData, 
      getAuthHeaders()
    );
    
    
    // Copy attributes to the new product
    if (response.data?.product?.product_id && productAttributes.length > 0) {
      const newProductId = response.data.product.product_id;
      
      // Add each attribute to the new product
      for (const attr of productAttributes) {
        try {
          const attributeId = attr.attribute_id || attr.id;
          if (attributeId) {
            await addAttributeToProduct(newProductId, attributeId);
          }
        } catch (error) {
          console.error(`Failed to add attribute ${attr.id} to duplicated product:`, error);
          // Continue with other attributes even if one fails
        }
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Error in duplicateProduct:', error);
    const errorMessage = handleApiError(error);
    throw new Error(`Failed to duplicate product: ${errorMessage}`);
  }
};
// Update product stock/inventory (admin)
export const updateProductStock = async (productId, quantity) => {
  try {
    const response = await axios.put(`${API_URL}/products/admin/products/${productId}/stock`, { quantity }, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Set main product image (admin)
export const setProductMainImage = async (productId, imageFileName) => {
  try {
    const response = await axios.put(
      `${API_URL}/products/admin/products/${productId}/main-image`, 
      { imageFileName },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Delete product image from gallery (admin)
export const deleteProductImage = async (productId, imageId) => {
  try {
    const response = await axios.delete(
      `${API_URL}/products/admin/products/${productId}/images/${imageId}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Get deleted product images (admin)
export const getDeletedProductImages = async (productId) => {
  try {
    const response = await axios.get(
      `${API_URL}/products/admin/products/${productId}/deleted-images`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Restore a deleted product image (admin)
export const restoreProductImage = async (productId, imageId) => {
  try {
    const response = await axios.put(
      `${API_URL}/products/admin/products/${productId}/images/${imageId}/restore`,
      {},
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Add an existing gallery image to a product
export const addGalleryImageToProduct = async (productId, filename, ordering) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_URL}/products/admin/products/${productId}/single-image`, 
      { file_name: filename, ordering }, // Match backend expected body
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data; // Should return { success, message, newImage, product }
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Upload a new image for a product
export const uploadProductImage = async (formData) => {
  try {
    const productId = formData.get('productId');
    const response = await axios.post(
      `${API_URL}/products/admin/products/${productId}/upload-image`,
      formData,
      {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Get gallery images for dropdown (admin)
export const getGalleryImages = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/products/admin/gallery-images`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Reorder product images (admin)
export const reorderProductImages = async (productId, orderingData) => {
  if (!productId) {
    throw new Error('Product ID is required');
  }
  try {
    // Extract the timestamp if provided
    const { imageOrdering, timestamp } = orderingData;
    // Ensure all IDs and ordering values are properly formatted as numbers
    const formattedOrdering = imageOrdering.map(item => ({
      id: typeof item.id === 'string' ? parseInt(item.id, 10) : item.id,
      ordering: typeof item.ordering === 'string' ? parseInt(item.ordering, 10) : item.ordering
    }));
    // Create the request data with the timestamp to ensure each request is unique
    const requestData = {
      imageOrdering: formattedOrdering,
      timestamp: timestamp || new Date().getTime() // Add timestamp to ensure uniqueness
    };
    const response = await axios.put(
      `${API_URL}/products/admin/products/${productId}/reorder-images?t=${requestData.timestamp}`,
      requestData,
      getAuthHeaders()
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reorder images');
    }
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Get product categories (admin)
export const getProductCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/products/admin/categories`, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Get all attributes (admin)
export const getAllAttributes = async () => {
  try {
    const response = await axios.get(`${API_URL}/products/admin/attributes`, getAuthHeaders());
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Get product attributes (admin)
export const getProductAttributes = async (productId) => {
  try {
    const response = await axios.get(
      `${API_URL}/products/admin/products/${productId}/attributes`, 
      getAuthHeaders()
    );
    
    
    // Normalize the response to a standard array format
    let attributes = [];
    
    if (Array.isArray(response.data)) {
      attributes = response.data;
    } else if (response.data && response.data.attributes && Array.isArray(response.data.attributes)) {
      attributes = response.data.attributes;
    } else if (response.data && response.data.product_id) {
      // If no attributes array, use empty array to avoid errors
      attributes = [];
    } else {
      // Unknown format, log and return empty array
      console.error(`Unexpected attribute format for product ${productId}:`, response.data);
      attributes = [];
    }
    
    
    // Normalize each attribute to ensure consistent ID fields
    const normalizedAttributes = attributes.map(attr => {
      if (!attr) return null;
      
      // Extract ID from any available field
      const id = (attr.attribute_id || attr.id || '').toString();
      
      // Ensure title is present
      const title = attr.title || attr.attribute_title || 'Unnamed Attribute';
      
      return {
        ...attr,
        // Ensure both ID fields exist as strings
        id: id,
        attribute_id: id,
        title: title
      };
    }).filter(Boolean); // Remove any null values
    
    
    // Return the attributes in a format expected by the CategoriesTab component
    return {
      attributes: normalizedAttributes
    };
  } catch (error) {
    console.error(`Error fetching attributes for product ${productId}:`, error);
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Add attribute to product (admin)
export const addAttributeToProduct = async (productId, attributeId) => {
  try {
    const response = await axios.post(
      `${API_URL}/products/admin/products/${productId}/attributes/${attributeId}`,
      {},
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
// Remove attribute from product (admin)
export const removeAttributeFromProduct = async (productId, attributeId) => {
  try {
    const response = await axios.delete(
      `${API_URL}/products/admin/products/${productId}/attributes/${attributeId}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};

// Get attribute filters (for product filtering)
export const getAttributeFilters = async (categoryId) => {
  try {
    let url = `${API_URL}/attributes/filters`;
    if (categoryId) {
      url += `?categoryId=${categoryId}`;
    }
    const response = await axios.get(url, getAuthHeaders());
    
    // Process response to ensure consistent ID format
    if (Array.isArray(response.data)) {
      const processedData = response.data.map(filter => {
        // Handle potentially missing ID fields
        const filterId = (filter.id || filter.attribute_id || '').toString();
        
        // Process children to ensure consistent attribute_id format
        const children = Array.isArray(filter.children) ? filter.children.map(child => {
          // Ensure we have a string ID from any available ID field
          const childId = (child.attribute_id || child.id || '').toString();
          
          return {
            ...child,
            // Always have both id and attribute_id as strings for consistent lookup
            id: childId,
            attribute_id: childId
          };
        }) : [];
        
        return {
          ...filter,
          id: filterId,
          attribute_id: filterId,
          children
        };
      });
      
      return processedData;
    }
    
    return response.data;
  } catch (error) {
    console.error("Error fetching attribute filters:", error);
    const errorMessage = handleApiError(error);
    throw new Error(errorMessage);
  }
};
